import Vue from 'vue';
import Router from 'vue-router';
import firebase from 'firebase';
import _ from 'lodash';

import EffectivePlugins from '@/ltnplugins/EffectivePlugins';

import API from '@/api';
import LoadBoards from '@/pages/LoadBoards';
import PostLoad from '@/pages/PostLoad';
import LoadDetail from '@/pages/Loads/Detail';
import MyAssignedLoads from '@/pages/User/MyAssignedLoads';
import CompanySettings from '@/pages/Companies/Settings';
import CompanyPlacedBids from '@/pages/Companies/PlacedBids';

import TransactionsPage from '@/pages/Companies/Transactions/TransactionsPage';

import ReceivingPage from '@/pages/Companies/Loads/ReceivingPage';
import Schedule from '@/pages/Companies/Receiving/Schedule';
import InTransit from '@/pages/Companies/Receiving/InTransit';
import Delivered from '@/pages/Companies/Receiving/Delivered';
import Arrivals from '@/pages/Companies/Receiving/Arrivals';
import Departures from '@/pages/Companies/Receiving/Departures';

import LoadsPage from '@/pages/Companies/Loads/LoadsPage';
import VRecurringLoadsSchedule from '@/pages/Companies/Loads/components/RecurringLoadsSchedule';
import VRepeatLoadTemplatesTool from '@/pages/Companies/Loads/components/RepeatLoadTemplatesTool';
import VDispatchedLoadRequests from '@/components/dashboard/simple/DispatchedLoadRequests';
import VDirectDispatchOverview from '@/pages/Companies/Loads/components/DirectDispatchOverview';

import NewCompany from '@/pages/Companies/NewCompany';
import QBAuth from '@/pages/Companies/QBAuth';
import XeroAuth from '@/pages/Companies/XeroAuth';
import CompanyList from '@/pages/Companies/CompanyList';
import UsersList from '@/pages/User/UsersList';
import Untethered from '@/pages/Companies/Untethered';
import ListingDetail from '@/pages/Listings/Detail';
import BeefPlantNumbers from '@/pages/BeefPlantNumbers';
import SavingsCalculator from '@/pages/SavingsCalculator';

import Profile from '@/pages/User/Profile';
import Onboard from '@/pages/User/Onboard';
import ChangePassword from '@/pages/User/ChangePassword';
import NotificationSettings from '@/pages/User/NotificationSettings';
import Refer from '@/pages/User/Refer';
import DispatchInvite from '@/pages/User/DispatchInvite';

import CompanyBase from '@/pages/Companies/CompanyBase';
import Invoices from '@/pages/Companies/Transactions/Invoices';
import Bills from '@/pages/Companies/Transactions/Bills';
import ApproveLoads from '@/ltnplugins/approveexport/ApproveLoads';
import ExportLoads from '@/ltnplugins/approveexport/ExportLoads';
import Error401 from '@/pages/Error401';
import PlanningSection from '@/pages/Companies/Loads/PlanningSection';
import HistorySection from '@/pages/Companies/Loads/HistorySection';
import LandingPage from '@/pages/LandingPage.vue';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'landing',
      component: LandingPage,
      meta: { authenticate: false },
    },
    {
      path: '/loadboard',
      name: 'load-boards',
      component: LoadBoards,
      props: route => ({ searchListingId: route.query.listingId }),
      meta: { authenticate: false },
    },
    {
      path: '/onboard',
      name: 'onboard',
      component: Onboard,
      props: route => ({ referrerParam: route.query.referrerParam }),
      meta: { authenticate: false },
    },
    {
      path: '/numbers',
      name: 'beef-plant-numbers',
      component: BeefPlantNumbers,
      meta: { authenticate: false, hide_sidebar: true },
    },
    {
      path: '/savings',
      name: 'savings',
      component: SavingsCalculator,
      props: route => ({ clientId: route.query.clientId }),
      meta: { authenticate: false, hide_sidebar: true },
    },
    {
      path: '/change-password',
      name: 'change-password',
      props: route => ({ code: route.query.oobCode }),
      component: ChangePassword,
      meta: { authenticate: false },
    },
    {
      path: '/claim-invitation/:token',
      name: 'claim-invitation',
      component: Onboard,
      props: route => ({ invitation: 'default', emailParam: route.query.email }),
      meta: { authenticate: false },
    },
    {
      path: '/claim-invitation/:token/driver',
      name: 'claim-invitation',
      component: Onboard,
      props: route => ({ invitation: 'driver', emailParam: route.query.email, driver: true }),
      meta: { authenticate: false },
    },
    {
      path: '/dispatch-invitation/:token',
      name: 'dispatch-invitation',
      component: DispatchInvite,
      props: route => ({ companyId: route.query.companyId }),
      meta: { authenticate: false },
    },
    {
      path: '/post-load/:id?',
      name: 'post-load',
      component: PostLoad,
      meta: { authenticate: true },
    },
    {
      path: '/profile/:userId?',
      name: 'profile',
      component: Profile,
      meta: { authenticate: true },
    },
    {
      path: '/refer',
      name: 'refer',
      component: Refer,
      meta: { authenticate: true },
    },
    {
      path: '/qb-auth',
      name: 'qb-auth',
      component: QBAuth,
      props: route => ({
        code: route.query.code,
        companyId: route.query.state,
        realmId: route.query.realmId,
      }),
      meta: { authenticate: false, hide_sidebar: true },
    },
    {
      path: '/xero-auth',
      name: 'xero-auth',
      component: XeroAuth,
      props: route => ({
        code: route.query.code,
        state: route.query.state,
        companyId: route.query.state,
      }),
      meta: { authenticate: false, hide_sidebar: true },
    },
    {
      path: '/notification-settings/:authProviderId?',
      name: 'notification-settings',
      component: NotificationSettings,
      props: route => ({ userId: route.query.userId }),
      meta: { authenticate: true },
    },
    {
      path: '/my-loads',
      name: 'my-loads',
      component: MyAssignedLoads,
      meta: { authenticate: true },
    },
    {
      path: '/loads/:loadId/detail',
      name: 'load.detail',
      component: LoadDetail,
      props: route => ({ loadNonce: route.query.ln, loadId: route.params.loadId }),
      meta: { authenticate: false },
    },
    {
      path: '/listings/:id/detail',
      name: 'listing.detail',
      component: ListingDetail,
      meta: { authenticate: false },
    },
    {
      path: '/companies',
      name: 'companies',
      component: CompanyList,
      meta: { authenticate: true },
    },
    {
      path: '/users',
      name: 'users',
      component: UsersList,
      meta: { authenticate: true },
    },
    {
      path: '/untethered',
      name: 'untethered',
      component: Untethered,
      meta: { authenticate: true },
    },
    {
      path: '/companies/new',
      name: 'new-company',
      component: NewCompany,
      meta: { authenticate: true },
    },
    {
      path: '/error',
      name: 'error401',
      component: Error401,
      meta: { authenticate: true },
    },
    {
      path: '/companies/:id/',
      name: 'company',
      props: true,
      component: CompanyBase,
      children: [
        {
          path: 'transactions/',
          name: 'company.transactions',
          redirect: { name: 'company.transactions.invoices' },
          component: TransactionsPage,
          meta: { authenticate: true },
          children: [
            {
              path: 'invoices/:txId?',
              name: 'company.transactions.invoices',
              component: Invoices,
              meta: { authenticate: true },
            },
            {
              path: 'bills/:txId?',
              name: 'company.transactions.bills',
              component: Bills,
              meta: { authenticate: true },
            },
            {
              path: 'approve/:loadId?',
              name: 'company.transactions.approve',
              component: ApproveLoads,
              meta: { authenticate: true },
            },
            {
              path: 'export/:loadId?',
              name: 'company.transactions.export',
              component: ExportLoads,
              meta: { authenticate: true },
            },
          ],
        },
        {
          path: 'loads/',
          name: 'company.loads',
          redirect: { name: 'company.loads.planner' },
          component: LoadsPage,
          meta: { authenticate: true },
          children: [
            {
              path: 'dispatch_offers/:loadId?',
              name: 'company.loads.dispatch_offers',
              component: VDispatchedLoadRequests,
              meta: {
                authenticate: true,
                title: 'Dispatch Requests',
              },
            },
            {
              path: 'recurring/:loadId?',
              name: 'company.loads.recurring',
              component: VRecurringLoadsSchedule,
              meta: {
                authenticate: true,
                title: 'Recurring Loads',
              },
            },
            {
              path: 'planner/:loadGroupId?',
              name: 'company.loads.planner',
              component: PlanningSection,
              meta: {
                authenticate: true,
                title: 'Planner',
              },
            },
            {
              path: 'repeat/:repeatLoadId?',
              name: 'company.loads.repeat',
              component: VRepeatLoadTemplatesTool,
              meta: {
                authenticate: true,
                title: 'Repeat Loads',
              },
            },
            {
              path: 'direct_dispatch/:loadId?',
              name: 'company.loads.direct_dispatch',
              component: VDirectDispatchOverview,
              meta: {
                authenticate: true,
                title: 'Direct Dispatch',
              },
            },
            {
              path: 'scheduled/:bookedLoadId?',
              name: 'company.loads.scheduled',
              component: Schedule,
              meta: {
                authenticate: true,
                title: 'Scheduled',
              },
            },
            {
              path: 'in_transit/:bookedLoadId?',
              name: 'company.loads.in_transit',
              component: InTransit,
              meta: {
                authenticate: true,
                title: 'In Transit',
              },
            },
            {
              path: 'delivered/:bookedLoadId?',
              name: 'company.loads.delivered',
              component: Delivered,
              meta: {
                authenticate: true,
                title: 'Delivered',
              },
            },
            {
              path: 'history',
              name: 'company.loads.history',
              component: HistorySection,
              meta: {
                authenticate: true,
                title: 'History',
              },
            },
          ],
        },
        {
          path: 'receiving/',
          name: 'company.receiving',
          redirect: { name: 'company.receiving.scheduled' },
          component: ReceivingPage,
          meta: { authenticate: true },
          children: [
            {
              path: 'scheduled/:bookedLoadId?',
              name: 'company.receiving.scheduled',
              component: Schedule,
              meta: {
                authenticate: true,
                title: 'Scheduled',
              },
            },
            {
              path: 'in_transit/:bookedLoadId?',
              name: 'company.receiving.in_transit',
              component: InTransit,
              meta: {
                authenticate: true,
                title: 'In Transit',
              },
            },
            {
              path: 'delivered/:bookedLoadId?',
              name: 'company.receiving.delivered',
              component: Delivered,
              meta: {
                authenticate: true,
                title: 'Delivered',
              },
            },
            {
              path: 'arrivals/:loadId?',
              name: 'company.receiving.arrivals',
              component: Arrivals,
              meta: {
                authenticate: true,
                title: 'Arrivals',
              },
            },
            {
              path: 'departures/:loadId?',
              name: 'company.receiving.departures',
              component: Departures,
              meta: {
                authenticate: true,
                title: 'Departures',
              },
            },
          ],
        },
        {
          path: 'profile',
          name: 'company.settings',
          component: CompanySettings,
          meta: { authenticate: true },
        },
        {
          // legacy path supported for emails and text messages
          // loads' planner
          path: 'listings',
          name: 'company.listings',
          meta: { authenticate: true },
          beforeEnter: (to, _from, next) => {
            next({ name: 'company.loads.planner', params: { id: to.params.id } });
          },
        },
        {
          path: 'placed-bids',
          name: 'company.placed-bids',
          component: CompanyPlacedBids,
          meta: { authenticate: true },
          props: route => ({ searchBidId: route.query.bidId }),
        },
        {
          // legacy path supported for emails and text messages.
          // should route to loads' scheduled section
          path: 'booked-loads',
          name: 'company.booked-loads',
          meta: { authenticate: true },
          beforeEnter: (to, _from, next) => {
            next({ name: 'company.loads.scheduled', params: { id: to.params.id } });
          },
        },
        {
          // legacy path supported for emails and text messages
          path: 'dispatched-loads',
          name: 'company.dispatched-loads',
          meta: { authenticate: true },
          beforeEnter: (to, _from, next) => {
            next({ name: 'company.loads.dispatch_offers', params: { id: to.params.id } });
          },
        },
        {
          // deprecated
          path: 'load-settlement',
          name: 'company.load-settlement',
          meta: { authenticate: true },
          beforeEnter: (to, _from, next) => {
            next({ name: 'company.transactions' });
          },
        },
      ],
    },
  ],
});

router.beforeEach(async (to, _from, next) => {
  const { token, ln, ...query } = to.query;
  if (token) {
    const response = await API.user.loginWithToken({ token });
    await firebase.auth().signInWithCustomToken(response.data.jwt);

    next({
      ...to,
      query,
      replace: true,
    });
  } else {
    next();
  }
});

const LOADING_TITLE = document.title;
let siteTitle = null;


router.afterEach((to, from) => {
  if (!siteTitle) {
    const designationResponse = API.siteDesignation.loadSiteDesignation();
    const siteDesignation = designationResponse.data;
    const effective = EffectivePlugins.assemble(siteDesignation);

    siteTitle = _.get(effective, 'hq.site_title', LOADING_TITLE);
  }

  // Use next tick to handle router history correctly
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  Vue.nextTick(() => {
    const title = _.get(to, 'meta.title', siteTitle) || LOADING_TITLE;
    document.title = title;
  });
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js